import React, {useEffect, useState} from 'react';
import {
  getAllProcesses,
  getAllSubProcesses,
  addKeyRisk,
} from '../../../services/keyRiskIndicatorsService';
import {getLanguage} from 'react-switch-lang';
import {t} from 'react-switch-lang';
import * as yup from 'yup';
import TextArea from '../../../components/FormFields/Textarea';
import './AddKeyRiskIndicator.scss';
import InputText from '../../../components/FormFields/InputText';
import {useHistory} from 'react-router-dom';
import {useForm, FormProvider} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers';
import SelectList from '../../../components/FormFields/Select';
import InputYear from '../../../components/FormFields/inputDate/InputYear';
import Toast from '../../../components/toast/Toast';
import periods from '../utils/periods';
import {validDouble} from '../../../utils/const';
import PageHeader from './PageHeader';
import SelectInfinite from '../../../components/FormFields/SelectInfinite';

const AddKeyRiskIndicator = (props) => {
  const lang = getLanguage();

  let history = useHistory();

  const fetchProcesses = async () => {
    try {
      const {data} = await getAllProcesses();
      return data;
    } catch (e) {
    }
  };

  useEffect(() => {
    fetchProcesses().then((r) => {
      let processes = [];
      r.forEach((item) => {
        processes.push(
          lang === 'eng'
            ? {value: item.id, label: item.nameEn}
            : {
              value: item.id,
              label: item.name,
            },
        );
      });
    });

  }, []);

  const schema = yup.object().shape({
    process: yup.object().nullable().required(t('message.fieldRequired')),
    subprocess: yup.object().nullable().required(t('message.fieldRequired')),
    subprocessDescriptionMne: yup
      .string()
      .required(t('message.fieldRequired'))
      .max(255, t('message.max', {max: 255})),
    subprocessDescriptionEn: yup
      .string()
      .required(t('message.fieldRequired'))
      .max(255, t('message.max', {max: 255})),
    riskIndicatorNameMne: yup
      .string()
      .required(t('message.fieldRequired'))
      .max(255, t('message.max', {max: 255})),
    riskIndicatorNameEn: yup
      .string()
      .required(t('message.fieldRequired'))
      .max(255, t('message.max', {max: 255})),
    escalationPointValue: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .matches(validDouble, t('message.onlyDecimalNumbers'))
      .typeError(t('message.onlyDecimalNumbers'))
      .max(20, t('message.max', {max: 20})),
    period: yup.object().nullable().required(t('message.fieldRequired')),
    escalationDescription: yup
      .string()
      .required(t('message.fieldRequired'))
      .max(255, t('message.max', {max: 255})),
    department: yup.object().nullable().required(t('message.fieldRequired')),
    riskDescriptionMne: yup
      .string()
      .required(t('message.fieldRequired'))
      .max(500, t('message.max', {max: 500})),
    riskDescriptionEn: yup
      .string()
      .required(t('message.fieldRequired'))
      .max(500, t('message.max', {max: 500})),
    measuresDescriptionMne: yup
      .string()
      .required(t('message.fieldRequired'))
      .max(500, t('message.max', {max: 500})),
    measuresDescriptionEn: yup
      .string()
      .required(t('message.fieldRequired'))
      .max(500, t('message.max', {max: 500})),
    year: yup.string().nullable().required(t('message.fieldRequired')),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
  });

  const {handleSubmit, watch, setValue} = methods;
  const processSelected = watch('process');

  const fetchSubprocesses = async () => {
    try {
      const {data} = await getAllSubProcesses(processSelected['value']);
      return data;
    } catch (e) {
    }
  };

  /*** Backend returns same array of subprocesses for first 2 selected processes so we cant rerender using watch on process ***/
  const [processChange, setProcessChange] = useState(false);

  useEffect(() => {
    /*** Get subprocesses on process change ***/
    if (processSelected) {

      if (props.location.state) {
        props.location.state.keyRisk.subprocess = '';
      }

      fetchSubprocesses().then((r) => {
        let subProcesses = [];
        r.forEach((item) => {
          subProcesses.push(
            lang === 'eng'
              ? {value: item.id, label: item.nameEn}
              : {
                value: item.id,
                label: item.name,
              },
          );
        });
        setProcessChange(true);
        setProcessChange(false);
      });
    }
    /*** If process cleared, clear subprocess ***/
    if (processSelected === null) {
      setProcessChange(true);
      setProcessChange(false);
      setValue('subprocess', '', {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [processSelected]);

  const goBack = () => {
    history.push('/key-risk-indicators');
  };

  const onSubmit = async (e) => {
    const date = new Date(e.year);
    const year = date.getFullYear();
    const body = {
      departmentId: e.department['value'],
      departmentName: lang === 'mne' ? e.department['label'] : '',
      departmentNameEn: lang === 'eng' ? e.department['label'] : '',
      description: e.escalationDescription,
      indicatorName: e.riskIndicatorNameMne,
      indicatorNameEn: e.riskIndicatorNameEn,
      indicatorPeriod: e.period['value'],
      measures: e.measuresDescriptionMne,
      measuresEn: e.measuresDescriptionEn,
      minValue: e.escalationPointValue,
      processId: e.process['value'],
      processName: lang === 'mne' ? e.process['label'] : '',
      processNameEn: lang === 'eng' ? e.process['label'] : '',
      riskDescription: e.riskDescriptionMne,
      riskDescriptionEn: e.riskDescriptionEn,
      subprocessDescription: e.subprocessDescriptionMne,
      subprocessDescriptionEn: e.subprocessDescriptionEn,
      subprocessId: e.subprocess['value'],
      subprocessName: lang === 'mne' ? e.subprocess['label'] : '',
      subprocessNameEn: lang === 'eng' ? e.subprocess['label'] : '',
      year: year,
    };
    try {
      const response = await addKeyRisk(body);
      if (response.status === 201) {
        await Toast.fire({
          title: t('Pages.messages.addSuccess', {
            entity:
              lang === 'eng'
                ? 'Key risk indicator'
                : 'Ključni indikator rizika',
          }),
          icon: 'success',
        });
        await history.push(`/key-risk-indicators`);
      }
    } catch (err) {
      if (err.response.data.code === 'indicator-exists') {
        Toast.fire({
          title:
            lang === 'eng'
              ? err.response.data.description
              : t('Pages.messages.sameIndicatorAdded'),
          icon: 'error',
        });
        document.getElementById('riskIndicatorNameMne').focus();
        document.getElementById('riskIndicatorNameMne').select();
      }
    }
  };

  return (
    <div>
      <PageHeader/>
      <div className="administration-template content">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-4 col-xl-3 pl-0">
                  <div className="title mb-3">
                    {t('Pages.AddKeyRiskIndicator.processesAndSubprocesses')}
                  </div>
                  <div className="form-group mb-4">
                    <label className="label" htmlFor="process">
                      {t('Pages.AddKeyRiskIndicator.processes')}
                    </label>
                    <SelectInfinite
                      api="processes"
                      name="process"
                      placeholder={t('common.select')}
                      labelKey={lang === 'mne' ? 'name' : 'nameEn'}
                      className="select"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.process
                          : ''
                      }
                    />
                  </div>
                  <div className="form-group mb-4">
                    <label className="label" htmlFor="subprocess">
                      {t('Pages.AddKeyRiskIndicator.subprocesses')}
                    </label>
                    <SelectInfinite
                      api={processSelected?.value !== undefined ? `/subprocesses?processId.equals=${processSelected?.value}` : `/subprocesses?processId.equals=1`}
                      name="subprocess"
                      placeholder={t('common.select')}
                      key={processChange}
                      // labelKey={lang === 'mne' ? 'name' : 'nameEn'}
                      className="select"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.subprocess
                          : ''
                      }
                      isDisabled={!processSelected}
                    />
                  </div>
                  <div className="form-group mb-4">
                    <label className="label" htmlFor="subprocessDescriptionMne">
                      {t('Pages.AddKeyRiskIndicator.subprocessesDescMne')}
                    </label>
                    <TextArea
                      name="subprocessDescriptionMne"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk
                            .subprocessDescriptionMne
                          : ''
                      }
                    />
                  </div>
                  <div className="form-group mb-4">
                    <label className="label" htmlFor="subprocessDescriptionEn">
                      {t('Pages.AddKeyRiskIndicator.subprocessesDescEn')}
                    </label>
                    <TextArea
                      name="subprocessDescriptionEn"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.subprocessDescriptionEn
                          : ''
                      }
                    />
                  </div>
                  <div className="title mb-3">
                    {t('Pages.AddKeyRiskIndicator.indicators')}
                  </div>
                  <div className="form-group mb-4">
                    <label className="label" htmlFor="riskIndicatorNameMne">
                      {t('Pages.AddKeyRiskIndicator.keyRiskNameMne')}
                    </label>
                    <InputText
                      name="riskIndicatorNameMne"
                      id="riskIndicatorNameMne"
                      className="form-control"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.riskIndicatorNameMne
                          : ''
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label className="label" htmlFor="riskIndicatorNameEn">
                      {t('Pages.AddKeyRiskIndicator.keyRiskNameEn')}
                    </label>
                    <InputText
                      name="riskIndicatorNameEn"
                      className="form-control"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.riskIndicatorNameEn
                          : ''
                      }
                    />
                  </div>
                </div>

                <div className="col-md-4 col-xl-3">
                  <div className="title mb-3">
                    {t('Pages.AddKeyRiskIndicator.escalation')}
                  </div>
                  <div className="row mb-4 d-flex flex-md-column flex-lg-row">
                    <div className="col-md-12 col-lg-6">
                      <label className="label" htmlFor="escalationPointValue">
                        {t('Pages.AddKeyRiskIndicator.escalationPointValue')}
                      </label>
                      <InputText
                        name="escalationPointValue"
                        type="text"
                        className="form-control"
                        defaultValue={
                          props.location.state
                            ? props.location.state.keyRisk.minValue
                            : ''
                        }
                      />
                    </div>
                    <div className="col-md-12 col-lg-6">
                      <label className="label" htmlFor="period">
                        {t('Pages.AddKeyRiskIndicator.escalationPointPeriod')}
                      </label>
                      <SelectList
                        name="period"
                        options={periods}
                        placeholder={t('common.select')}
                        defaultValue={
                          props.location.state
                            ? props.location.state.keyRisk.indicatorPeriod
                            : ''
                        }
                      />
                    </div>
                  </div>
                  <div className="form-group mb-4">
                    <label className="label" htmlFor="escalationDescription">
                      {t('Pages.AddKeyRiskIndicator.escalationPointDesc')}
                    </label>
                    <TextArea
                      name="escalationDescription"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.description
                          : ''
                      }
                    />
                  </div>
                  <div className="title mb-3">
                    {t('Pages.AddKeyRiskIndicator.organization')}
                  </div>
                  <div className="form-group mb-4">
                    <label className="label" htmlFor="department">
                      {t('Pages.AddKeyRiskIndicator.department')}
                    </label>
                    <SelectInfinite
                      api="departments"
                      name="department"
                      placeholder={t('common.select')}
                      labelKey={lang === 'mne' ? 'name' : 'nameEn'}
                      className="select"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.department
                          : ''
                      }
                    />
                  </div>
                  <div className="title mb-3">
                    {t('Pages.AddKeyRiskIndicator.risk')}
                  </div>
                  <div className="form-group mb-4">
                    <label className="label" htmlFor="riskDescriptionMne">
                      {t('Pages.AddKeyRiskIndicator.riskDescMne')}
                    </label>
                    <TextArea
                      name="riskDescriptionMne"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.riskDescription
                          : ''
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label className="label" htmlFor="riskDescriptionEn">
                      {t('Pages.AddKeyRiskIndicator.riskDescEn')}
                    </label>
                    <TextArea
                      name="riskDescriptionEn"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.riskDescriptionEn
                          : ''
                      }
                    />
                  </div>
                </div>

                <div className="col-md-4 col-xl-3">
                  <div className="title mb-3">
                    {t('Pages.AddKeyRiskIndicator.measures')}
                  </div>
                  <div className="form-group mb-4">
                    <label className="label" htmlFor="measuresDescriptionMne">
                      {t('Pages.AddKeyRiskIndicator.measuresDescMne')}
                    </label>
                    <TextArea
                      name="measuresDescriptionMne"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.measuresDescription
                          : ''
                      }
                    />
                  </div>
                  <div className="form-group mb-4">
                    <label className="label" htmlFor="measuresDescriptionEn">
                      {t('Pages.AddKeyRiskIndicator.measuresDescEn')}
                    </label>
                    <TextArea
                      name="measuresDescriptionEn"
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.measuresDescriptionEn
                          : ''
                      }
                    />
                  </div>
                  <div className="title mb-3">
                    {t('Pages.AddKeyRiskIndicator.year')}
                  </div>
                  <div className="form-group d-flex flex-column">
                    <label className="label" htmlFor="year">
                      {t('Pages.AddKeyRiskIndicator.year')}
                    </label>
                    <InputYear
                      name="year"
                      key={props.location.state}
                      defaultValue={
                        props.location.state
                          ? props.location.state.keyRisk.year
                          : ''
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="container-fluid mt-4">
              <div className="row">
                <div className="col-12 pt-4 pb-4 d-flex justify-content-end footer">
                  <button
                    className="secondary-btn"
                    onClick={(event) => {
                      event.preventDefault();
                      goBack();
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                  <button className="primary-btn">{t('common.save')}</button>
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default AddKeyRiskIndicator;
